import React, { Component, createRef, Fragment } from 'react';
import { ButtonTheme, Key } from '@philips-internal/oswald-ui-constants';
import { ReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { AbortableReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types/readable.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { REQUIRED } from '@philips-internal/oswald-ui-model/lib/validators/index.js';
import { createAbort, isDefined, isEmptyArrayOrObject, isFunction, isMobile } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as RotateDeviceIcon } from '../../../resources/assets/components/DLS_RotateDevice.svg';
import { ReactComponent as VertexIcon } from '../../../resources/assets/components/DLS_TopLeftVertex.svg';
import { Autocomplete } from '../autocomplete/autocomplete.js';
import { Button } from '../button/button.js';
import { Field } from '../field/field.js';
import { Form } from '../form/form.js';
import { Input } from '../input/input.js';
import { Loader } from '../loader/loader.js';
import { Mapper } from '../mapper/mapper.js';
import { Text } from '../text/text.js';
import { TextArea } from '../text-area/text-area.js';
import { BarcodeReaderType, BarcodeType } from './barcode-constants.js';
import { disposeMediaStream } from './barcode-utils.js';
import { InfraredContent } from './components/infrared-content.js';
import { InputActions } from './components/input-actions.js';
import './barcode-content.css';
let barcodeFormatToTypeMap = new Map();
const requiredValidator = [
    { name: REQUIRED },
];
const emptyManualInputData = {
    description: '',
    reason: {
        description: '',
        id: '',
    },
    value: '',
};
class BarcodeContent extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            cameraReady: false,
            isImporting: true,
            loading: false,
        };
        this.formRef = createRef();
        this.videoRef = createRef();
        this.infraredContentRef = createRef();
        /**
         * Control if a decoding is already running, to prevent memory leaks.
         */
        this.isDecoding = false;
        /**
         * Store the media-stream directly within the component instance, to prevent conditions not being met
         * due to React's async nature.
         */
        this.mediaStream = null;
        /**
         * Stores the barcode reader constructor when it is imported.
         */
        this.browserMultiFormatReader = null;
        /**
         * Store the barcode reader directly within the component instance, to prevent conditions not being met
         * due to React's async nature.
         */
        this.reader = null;
        this.disposeMediaStream = () => {
            if (!this.mediaStream) {
                return;
            }
            disposeMediaStream(this.mediaStream);
            this.mediaStream = null;
        };
        this.disposeReader = () => {
            if (this.reader) {
                this.reader.stopAsyncDecode();
                this.reader.stopContinuousDecode();
            }
            this.isDecoding = false;
        };
        this.requestMediaStream = () => {
            const constraints = {
                video: {
                    facingMode: 'environment',
                    frameRate: 60,
                },
            };
            this.setState({
                loading: true,
            }, () => {
                const mediaRequestPromise = navigator.mediaDevices.getUserMedia(constraints);
                mediaRequestPromise
                    .then(this.onMediaRequestSuccess)
                    .catch(this.onMediaRequestError)
                    .finally(() => {
                    this.setState({ loading: false });
                });
            });
        };
        this.startDecoding = () => {
            if (this.videoRef.current === null || !this.mediaStream || !this.browserMultiFormatReader) {
                return;
            }
            this.disposeReader();
            this.reader = new this.browserMultiFormatReader();
            this.isDecoding = true;
            void this.reader.decodeOnceFromStream(this.mediaStream, this.videoRef.current)
                .then(this.onDecodingSuccess)
                .catch(this.onDecodingError);
        };
        this.onDecodingSuccess = (result) => {
            const { onClose, onReadSuccess } = this.props;
            this.isDecoding = false;
            if (!isFunction(onReadSuccess)) {
                return;
            }
            const libraryFormat = result.getBarcodeFormat();
            const text = result.getText();
            const callback = () => {
                const abort = createAbort();
                const item = {
                    type: barcodeFormatToTypeMap.get(libraryFormat) ?? BarcodeType.UNKNOWN,
                    value: text,
                };
                onReadSuccess(new AbortableReadEvent({ abort, item }));
                void abort.promise
                    .then(aborted => {
                    if (aborted) {
                        return this.startDecoding();
                    }
                    this.disposeMediaStream();
                    this.disposeReader();
                    if (isFunction(onClose)) {
                        onClose();
                    }
                })
                    .finally(() => this.setState({ loading: false }));
            };
            this.setState({ loading: true }, callback);
        };
        this.onDecodingError = (response) => {
            const { onReadFailure } = this.props;
            if (!isFunction(onReadFailure)) {
                return;
            }
            onReadFailure(new ReadEvent({ item: response }));
        };
        this.onCameraButtonClick = () => {
            const { onReadTypeChange } = this.props;
            this.disposeMediaStream();
            this.disposeReader();
            this.setState({ currentReadType: BarcodeReaderType.CAMERA }, () => {
                this.requestMediaStream();
                if (isFunction(onReadTypeChange)) {
                    onReadTypeChange(BarcodeReaderType.CAMERA);
                }
            });
        };
        this.onInfraredButtonClick = () => {
            const { onReadTypeChange } = this.props;
            this.disposeMediaStream();
            this.disposeReader();
            this.setState({ cameraReady: false, currentReadType: BarcodeReaderType.INFRARED }, () => {
                if (isFunction(onReadTypeChange)) {
                    onReadTypeChange(BarcodeReaderType.INFRARED);
                }
            });
        };
        this.onManualButtonClick = () => {
            const { onReadTypeChange } = this.props;
            this.disposeMediaStream();
            this.disposeReader();
            this.setState({ cameraReady: false, currentReadType: BarcodeReaderType.MANUAL }, () => {
                if (isFunction(onReadTypeChange)) {
                    onReadTypeChange(BarcodeReaderType.MANUAL);
                }
            });
        };
        this.onInfraredButtonSubmitClick = (data) => {
            const { onInfraredButtonSubmitClick, onReadSuccess } = this.props;
            if (!isFunction(onReadSuccess)) {
                return;
            }
            this.setState({ loading: true }, () => {
                const item = {
                    type: BarcodeType.INFRARED,
                    value: data.value,
                };
                const abort = createAbort();
                onReadSuccess(new AbortableReadEvent({ abort, item }));
                void abort.promise
                    .then(aborted => {
                    if (aborted) {
                        return this.infraredContentRef.current?.reset();
                    }
                    this.infraredContentRef.current?.reset();
                    if (isFunction(onInfraredButtonSubmitClick)) {
                        onInfraredButtonSubmitClick();
                    }
                })
                    .finally(() => this.setState({ loading: false }));
            });
        };
        this.onManualButtonSubmitClick = () => {
            const { onReadSuccess, onManualButtonSubmitClick } = this.props;
            const { manualInputData, manualInputValid } = this.state;
            if (!manualInputValid || !isFunction(onReadSuccess)) {
                return;
            }
            this.setState({ loading: true }, () => {
                const item = {
                    description: manualInputData?.description ?? '',
                    reason: manualInputData?.reason?.id ?? '',
                    type: BarcodeType.MANUAL,
                    value: manualInputData?.value ?? '',
                };
                const abort = createAbort();
                onReadSuccess(new AbortableReadEvent({ abort, item }));
                void abort.promise
                    .then(aborted => {
                    if (aborted) {
                        return this.setState({
                            manualInputData: emptyManualInputData,
                            manualInputValid: undefined,
                        });
                    }
                    this.setState({
                        manualInputData: undefined,
                        manualInputValid: undefined,
                    }, () => {
                        if (isFunction(onManualButtonSubmitClick)) {
                            onManualButtonSubmitClick();
                        }
                    });
                })
                    .finally(() => this.setState({ loading: false }));
            });
        };
        this.onManualFormChange = (event) => {
            this.setState({ manualInputData: event.value });
        };
        this.onManualFormValidate = (event) => {
            this.setState({ manualInputValid: event.valid });
        };
        this.onMediaRequestSuccess = (mediaStream) => {
            const { onReadTypeChange } = this.props;
            this.mediaStream = mediaStream;
            this.setState({
                cameraReady: true,
                currentReadType: BarcodeReaderType.CAMERA,
            }, () => {
                if (isFunction(onReadTypeChange)) {
                    onReadTypeChange(BarcodeReaderType.CAMERA);
                }
            });
        };
        this.onMediaRequestError = (response) => {
            const { onReadFailure } = this.props;
            if (!isFunction(onReadFailure)) {
                return;
            }
            onReadFailure(new ReadEvent({ item: response }));
        };
        this.renderCameraContent = () => {
            const { cameraNotSupportedExpression = BarcodeContent.defaultProps.cameraNotSupportedExpression } = this.props;
            const { cameraReady, currentReadType, loading } = this.state;
            if (currentReadType !== BarcodeReaderType.CAMERA) {
                return null;
            }
            if (loading || !cameraReady || !this.mediaStream) {
                return this.renderLoader('c-barcode__camera-loader');
            }
            return (React.createElement("div", { className: "c-barcode__camera t-color-signal-green" },
                React.createElement("div", { className: "c-barcode__camera-overlay" },
                    React.createElement("div", { className: "c-barcode__camera-overlay-strip c-barcode__camera-overlay-strip--top" }, this.renderTopStripContent()),
                    React.createElement("div", { className: "c-barcode__camera-overlay-center" }, this.renderCenterCameraContent()),
                    React.createElement("div", { className: "c-barcode__camera-overlay-strip c-barcode__camera-overlay-strip--bottom" },
                        React.createElement("div", { className: "c-barcode__content-actions" }, this.renderInputActions()))),
                React.createElement("video", { playsInline: true, ref: this.videoRef }, MessageService.translate(cameraNotSupportedExpression))));
        };
        this.renderInfraredContent = () => {
            if (this.state.currentReadType !== BarcodeReaderType.INFRARED) {
                return null;
            }
            return (React.createElement(InfraredContent, { ...this.props, inputActions: this.renderInputActions(), onSubmitButtonClick: this.onInfraredButtonSubmitClick, ref: this.infraredContentRef }));
        };
        this.renderManualContent = () => {
            const { manualInputSubmitExpression } = this.props;
            const { currentReadType, manualInputData: manualInputRecord = {}, manualInputValid } = this.state;
            if (currentReadType !== BarcodeReaderType.MANUAL) {
                return null;
            }
            const defaultFieldProperties = {
                alwaysShowLabel: true,
                uuid: 'fake-uuid',
                width: 12,
            };
            return (React.createElement("div", { className: "c-barcode__manual" },
                React.createElement(Form, { className: "c-barcode__manual-form", data: manualInputRecord, disabled: true, editing: true, locked: true, onChange: this.onManualFormChange, onValidate: this.onManualFormValidate, ref: this.formRef, uuid: "fake-uuid" },
                    this.renderManualInput(defaultFieldProperties),
                    this.renderManualReason(defaultFieldProperties),
                    this.renderManualDescription(defaultFieldProperties)),
                React.createElement("div", { className: "c-barcode__content-actions" },
                    React.createElement(Button, { enabled: manualInputValid, key: "barcode-button-manual-submit", onClick: this.onManualButtonSubmitClick, theme: ButtonTheme.PRIMARY, value: manualInputSubmitExpression }),
                    this.renderInputActions())));
        };
        this.onFieldKeyDown = (event) => {
            if (event.key !== Key.ENTER) {
                return;
            }
            const { manualInputValid } = this.state;
            if (!manualInputValid) {
                this.formRef.current?.validate();
                return;
            }
            this.onManualButtonSubmitClick();
        };
        this.renderManualInput = (defaultFieldProperties) => {
            const { manualInputBarcodeExpression, formatters, manualInputMaxLength } = this.props;
            return (React.createElement(Field, { ...defaultFieldProperties, entry: "value", label: manualInputBarcodeExpression, onKeyDown: this.onFieldKeyDown, validators: requiredValidator },
                React.createElement(Input, { autoFocus: true, enforceLength: isDefined(manualInputMaxLength), formatters: formatters, length: manualInputMaxLength })));
        };
        this.renderManualDescription = (defaultFieldProperties) => {
            const { hasDescription, manualInputDescriptionExpression, requireDescription } = this.props;
            if (!hasDescription) {
                return null;
            }
            const fieldProperties = { ...defaultFieldProperties };
            if (requireDescription) {
                Object.assign(fieldProperties, { validators: requiredValidator });
            }
            return (React.createElement(Field, { ...fieldProperties, entry: "description", label: manualInputDescriptionExpression },
                React.createElement(TextArea, { length: 256 })));
        };
        this.renderManualReason = (defaultFieldProperties) => {
            const { data, manualInputReasonExpression, manualInputReasonEmptyResultExpression, manualInputReasonPlaceholderExpression, requireReason } = this.props;
            if (isEmptyArrayOrObject(data)) {
                return null;
            }
            // Need to cast because the Autocomplete compoenent does not support generics.
            const reasons = data;
            const autocompleteProperties = {
                data: reasons,
                displayEntry: 'description',
                emptyResultExpression: manualInputReasonEmptyResultExpression,
                entry: 'description',
                localData: true,
                minSearchLength: 0,
                placeholder: manualInputReasonPlaceholderExpression,
                title: manualInputReasonExpression,
                uuid: 'fake-uuid',
            };
            const fieldProperties = { ...defaultFieldProperties };
            if (requireReason) {
                Object.assign(fieldProperties, { validators: requiredValidator });
            }
            return (React.createElement(Field, { ...fieldProperties, entry: "reason", label: manualInputReasonExpression },
                React.createElement(Autocomplete, { ...autocompleteProperties },
                    React.createElement(Mapper, { map: { value: 'description' } },
                        React.createElement(Text, null)))));
        };
    }
    async componentDidMount() {
        const { defaultReadType } = this.props;
        this.setState({
            currentReadType: defaultReadType,
        }, () => {
            if (defaultReadType === BarcodeReaderType.CAMERA) {
                this.requestMediaStream();
            }
        });
        const { BarcodeFormat, BrowserMultiFormatReader } = await import('@zxing/library');
        this.browserMultiFormatReader = BrowserMultiFormatReader;
        barcodeFormatToTypeMap = new Map([
            [BarcodeFormat.ITF, BarcodeType.CODE_25],
            [BarcodeFormat.CODE_39, BarcodeType.CODE_39],
            [BarcodeFormat.CODE_128, BarcodeType.CODE_128],
            [BarcodeFormat.DATA_MATRIX, BarcodeType.DATA_MATRIX],
            [BarcodeFormat.EAN_13, BarcodeType.EAN_13],
            [BarcodeFormat.QR_CODE, BarcodeType.QR_CODE],
        ]);
        this.setState({ isImporting: false });
    }
    componentDidUpdate() {
        const { cameraReady, currentReadType } = this.state;
        const shouldStartDecoding = cameraReady && currentReadType === BarcodeReaderType.CAMERA && !this.isDecoding;
        if (shouldStartDecoding) {
            this.startDecoding();
        }
    }
    componentWillUnmount() {
        const { onClose } = this.props;
        if (isFunction(onClose)) {
            onClose();
        }
        this.disposeMediaStream();
        this.disposeReader();
    }
    renderInputActions() {
        return (React.createElement(InputActions, { cameraInputExpression: this.props.cameraInputExpression, currentReadType: this.state.currentReadType, infraredInputExpression: this.props.infraredInputExpression, loading: this.state.loading, manualInputExpression: this.props.manualInputExpression, onCameraButtonClick: this.onCameraButtonClick, onInfraredButtonClick: this.onInfraredButtonClick, onManualButtonClick: this.onManualButtonClick, supportCamera: this.props.supportCamera, supportInfrared: this.props.supportInfrared }));
    }
    renderTopStripContent() {
        const { cameraStripTopTitleExpression = BarcodeContent.defaultProps.cameraStripTopTitleExpression } = this.props;
        return (React.createElement(Fragment, null,
            React.createElement("span", { className: "c-barcode__overlay-title" }, MessageService.translate(cameraStripTopTitleExpression)),
            isMobile() && React.createElement(RotateDeviceIcon, { className: "c-barcode__rotate-device-icon" })));
    }
    renderCenterCameraContent() {
        return (React.createElement(Fragment, null,
            React.createElement("div", { className: "c-barcode__camera-overlay-header" },
                React.createElement(VertexIcon, { className: "c-barcode__camera-overlay-icon c-barcode__camera-overlay-icon--top-left" }),
                React.createElement(VertexIcon, { className: "c-barcode__camera-overlay-icon c-barcode__camera-overlay-icon--top-right" })),
            React.createElement("div", { className: "c-barcode__camera-overlay-center-strip" }),
            React.createElement("div", { className: "c-barcode__camera-overlay-footer" },
                React.createElement(VertexIcon, { className: "c-barcode__camera-overlay-icon c-barcode__camera-overlay-icon--bottom-left" }),
                React.createElement(VertexIcon, { className: "c-barcode__camera-overlay-icon c-barcode__camera-overlay-icon--bottom-right" }))));
    }
    renderLoader(className) {
        return (React.createElement(Loader, { centered: true, className: className, size: isMobile() ? 32 : 64 }));
    }
    render() {
        const { currentReadType, loading } = this.state;
        if (!currentReadType) {
            return null;
        }
        if (loading || this.state.isImporting) {
            return this.renderLoader('c-barcode__content-loader');
        }
        const readerContent = {
            [BarcodeReaderType.CAMERA]: this.renderCameraContent,
            [BarcodeReaderType.INFRARED]: this.renderInfraredContent,
            [BarcodeReaderType.MANUAL]: this.renderManualContent,
        }[currentReadType];
        return (React.createElement("div", { className: "c-barcode__content" }, readerContent()));
    }
}
BarcodeContent.defaultProps = {
    cameraInputExpression: 'tws.barcode.camera-input',
    cameraNotSupportedExpression: 'tws.barcode.no-camera-support',
    cameraStripTopTitleExpression: 'tws.barcode.camera-strip-top-title',
    data: [],
    defaultReadType: BarcodeReaderType.MANUAL,
    hasDescription: true,
    infraredInputExpression: 'tws.barcode.infrared-input',
    infraredNotSupportedExpression: 'tws.barcode.no-infrared-support',
    manualInputBarcodeExpression: 'tws.barcode.manual-entry-code',
    manualInputDescriptionExpression: 'tws.barcode.manual-entry-description',
    manualInputExpression: 'tws.barcode.manual-input',
    manualInputReasonEmptyResultExpression: 'tws.not-found.results-not-found',
    manualInputReasonExpression: 'tws.barcode.manual-entry-reason',
    manualInputReasonPlaceholderExpression: '',
    manualInputSubmitExpression: 'tws.barcode.manual-submit',
    requireDescription: true,
    requireReason: true,
    supportCamera: true,
    supportInfrared: false,
};
export { BarcodeContent, };
//# sourceMappingURL=barcode-content.js.map